import { safeParse } from 'valibot'
import {
  WORKSPACE_MAPS_INDEX_PATH,
  WORKSPACE_MAPS_LOAD_PATH,
  WORKSPACE_MAPS_SAVE_PATH,
} from './workspace_maps.api.constants'
import {
  WorkspaceMapIndexResponseSchema,
  WorkspaceMapLoadRequestSchema,
  WorkspaceMapLoadResponseSchema,
  WorkspaceMapSaveRequestSchema,
  WorkspaceMapSaveResponseSchema,
  WorkspaceMapLegacyFileSchema,
  type WorkspaceMapEntry,
  type WorkspaceMapIndexEntry,
} from './workspace_maps.type'
import type { MapRowValue } from '../../../backend.d'
import { decodeWorkspacePersistedStateData, encodeWorkspacePersistedStateBytes } from './workspacePersistedState'

const WORKSPACE_MAPS_CLIENT_ERROR = 'Workspace service is only available in Vite dev mode'

function assertWorkspaceMapsClientAvailable(): void {
  if (!import.meta.env.DEV) {
    throw new Error(WORKSPACE_MAPS_CLIENT_ERROR)
  }
}

async function readJsonResponse(response: Response): Promise<unknown> {
  const rawText = await response.text()
  if (!rawText) {
    return null
  }

  try {
    return JSON.parse(rawText) as unknown
  } catch {
    throw new Error(`Invalid JSON response from workspace maps endpoint: ${rawText.slice(0, 200)}`)
  }
}

async function fetchWorkspaceMapsJson(path: string, init?: RequestInit): Promise<unknown> {
  assertWorkspaceMapsClientAvailable()
  if (typeof fetch !== 'function') {
    throw new Error('Workspace service requires fetch')
  }

  const response = await fetch(path, {
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
  })
  const payload = await readJsonResponse(response)

  if (!response.ok) {
    const errorMessage = response.statusText || 'Workspace request failed'
    if (typeof payload === 'object' && payload !== null && typeof (payload as { error?: unknown }).error === 'string') {
      throw new Error((payload as { error: string }).error)
    }
    throw new Error(errorMessage)
  }

  return payload
}

export async function listWorkspaceMaps(): Promise<WorkspaceMapIndexEntry[]> {
  const payload = await fetchWorkspaceMapsJson(WORKSPACE_MAPS_INDEX_PATH, { method: 'GET' })
  const responseParseResult = safeParse(WorkspaceMapIndexResponseSchema, payload)
  if (!responseParseResult.success) {
    throw new Error('Invalid workspace map list response')
  }

  return responseParseResult.output.workspaceMapIndex
}

export type WorkspaceMapRowValue = MapRowValue & {
  file_id: string
}

export async function listWorkspaceMapRows(): Promise<WorkspaceMapRowValue[]> {
  const workspaceMapEntries = await Promise.all(
    (await listWorkspaceMaps()).map(async ({ file_id }) => loadWorkspaceMap(file_id))
  )

  return workspaceMapEntries.map(({ file_id, row }) => ({
    ...row,
    data: decodeWorkspacePersistedStateData(row.data),
    file_id,
  }))
}

export async function loadWorkspaceMap(file_id: string): Promise<WorkspaceMapEntry> {
  const requestParseResult = safeParse(WorkspaceMapLoadRequestSchema, { file_id })
  if (!requestParseResult.success) {
    throw new Error(`Invalid workspace map file id: ${file_id}`)
  }

  const payload = await fetchWorkspaceMapsJson(WORKSPACE_MAPS_LOAD_PATH, {
    method: 'POST',
    body: JSON.stringify(requestParseResult.output),
  })

  const responseParseResult = safeParse(WorkspaceMapLoadResponseSchema, payload)
  if (!responseParseResult.success) {
    const legacyParseResult = safeParse(WorkspaceMapLegacyFileSchema, payload && typeof payload === 'object' && 'entry' in (payload as Record<string, unknown>)
      ? (payload as Record<string, unknown>).entry
      : payload)
    if (!legacyParseResult.success) {
      throw new Error(`Invalid workspace map load response: ${file_id}`)
    }
    return {
      file_id,
      row: {
        ...legacyParseResult.output,
        data: encodeWorkspacePersistedStateBytes(legacyParseResult.output.data),
      },
    }
  }

  return responseParseResult.output.entry
}

export async function loadWorkspaceMapRow(file_id: string): Promise<WorkspaceMapRowValue> {
  const entry = await loadWorkspaceMap(file_id)
  return {
    ...entry.row,
    data: decodeWorkspacePersistedStateData(entry.row.data),
    file_id: entry.file_id,
  }
}

export async function saveWorkspaceMap(entry: WorkspaceMapEntry): Promise<WorkspaceMapEntry> {
  const requestParseResult = safeParse(WorkspaceMapSaveRequestSchema, { entry })
  if (!requestParseResult.success) {
    throw new Error(`Invalid workspace map save request: ${entry.file_id}`)
  }

  const payload = await fetchWorkspaceMapsJson(WORKSPACE_MAPS_SAVE_PATH, {
    method: 'POST',
    body: JSON.stringify(requestParseResult.output),
  })

  const responseParseResult = safeParse(WorkspaceMapSaveResponseSchema, payload)
  if (!responseParseResult.success) {
    throw new Error(`Invalid workspace map save response: ${entry.file_id}`)
  }

  return responseParseResult.output.entry
}
